// Copyright 2018 Sorama B.V.

import { MeasurementProcessorTypes } from "datatypes/MeasurementProcessorTypes";
import { Point3D } from "datatypes/SoramaLocalApi";
import { GenericMeasurement, MeasurementPartType } from "datatypes/SoramaWebApi";

export class MeasurementExtensions {
    // In memory cache to store device microphone positions by position hash
    // Note: This caches only the physical positions of microphones on devices,
    // not user selections. User selections should not persist across measurements.
    public static micPositionsCache: Map<string, Point3D[]> = new Map();

    public static toProcessorName(measurement: GenericMeasurement): string {
        if (!measurement.computedMeasurementProperties) throw new Error("Measurement is not a computed measurement");

        switch (measurement.computedMeasurementProperties.ProcessorType) {
            case MeasurementProcessorTypes.beamformProbe:
                return "Far Field Analysis Probe";
            case MeasurementProcessorTypes.timeRange:
                return "Time Range";
            default:
                return "";
        }
    }

    public static toFileName(measurement: GenericMeasurement): string {
        const regex = /[^a-zA-Z0-9_\-\+()=]/g;

        return measurement.name.replace(regex, "_");
    }

    public static getNumberOfChannels(measurement: GenericMeasurement): number {
        return measurement.parts
            .filter((x) => x.measurementType === MeasurementPartType.Audio)
            .map((x) => x.device.sensorPositions.length)
            .reduce((x, y) => x + y);
    }

    /** Turns the mic index into the subIndex format, e.g. "0.2, 0.4, 0.6". Returns "" if measurement only has one channel */
    public static micToSubIndexString(measurement: GenericMeasurement, mic: Point3D): string {
        return MeasurementExtensions.getNumberOfChannels(measurement) > 1
            ? mic.x.toFixed(2) + "," + mic.y.toFixed(2) + "," + mic.z.toFixed(2)
            : "";
    }

    /**
     * Returns the subindex display name, e.g. "Point(0.2, 0.4, 0.6)"
     * If the measurement only has one channel, the measurement name is returned
     */
    public static getDisplaySubIndexDisplayName(measurement: GenericMeasurement, subIdx: string): string {
        return MeasurementExtensions.getNumberOfChannels(measurement) > 1 ? "Point(" + subIdx + ")" : measurement.name;
    }

    /**
     * Creates a unique hash for the microphone positions of a measurement
     * @param measurement The measurement to create a hash for
     * @returns A string hash representing the unique microphone configuration
     */
    public static getMicrophonePositionsHash(measurement: GenericMeasurement): string {
        // Get only the first audio part - assumes all parts have same config
        const audioPart = measurement.parts.find((x) => x.measurementType === MeasurementPartType.Audio);
        if (!audioPart) return "";

        const { device, position } = audioPart;

        // Create a device signature that includes array size
        const deviceSignature = `${device.sensorPositions.length}:${position.x},${position.y},${position.z}`;

        // For large arrays, sample only the boundary positions to create the hash
        if (device.sensorPositions.length > 100) {
            const first = device.sensorPositions[0];
            const last = device.sensorPositions[device.sensorPositions.length - 1];
            return `${deviceSignature}|${first.x},${first.y},${first.z}|${last.x},${last.y},${last.z}`;
        }

        // For smaller arrays, use all positions
        return `${deviceSignature}|${device.sensorPositions.map((p) => `${p.x},${p.y},${p.z}`).join("|")}`;
    }

    private static calculateMicrophonePositions(measurement: GenericMeasurement): Point3D[] {
        return [].concat(
            ...measurement.parts
                .filter((x) => x.measurementType === MeasurementPartType.Audio)
                .map((x) => {
                    return x.device.sensorPositions.map((mic) => {
                        return <Point3D>{
                            x: x.position.x + mic.x,
                            y: x.position.y + mic.y,
                            z: x.position.z + mic.z,
                        };
                    });
                })
        );
    }

    public static getMicrophonePositionsForMeasurement(measurement: GenericMeasurement): Point3D[] {
        const positionsHash = this.getMicrophonePositionsHash(measurement);

        // Check cache first
        if (this.micPositionsCache.has(positionsHash)) {
            return this.micPositionsCache.get(positionsHash);
        }

        const positions = this.calculateMicrophonePositions(measurement);
        this.micPositionsCache.set(positionsHash, positions);
        return positions;
    }

    // Method to clear cache if needed
    public static clearMicrophonePositionsCache(): void {
        this.micPositionsCache.clear();
    }

    /**
     * Get all measurements that have the same microphone positions as the provided ground truth measurement
     * @param groundTruth
     * @param measurements
     * @returns
     **/
    public static getMeasurementsWithSameMicPositions(
        groundTruth: GenericMeasurement,
        measurements: GenericMeasurement[]
    ): GenericMeasurement[] {
        const groundTruthHash = MeasurementExtensions.getMicrophonePositionsHash(groundTruth);

        return measurements.filter(
            (measurement) => MeasurementExtensions.getMicrophonePositionsHash(measurement) === groundTruthHash
        );
    }

    /**
     * Check if the provided measurement is related to the provided measurement id
     * @param measurement
     * @param measurementId
     */
    public static isRelatedTo(measurement: GenericMeasurement, measurementId: string): boolean {
        if (!measurement) return false;

        if (measurement.id === measurementId) return true;

        let parent = measurement.computedMeasurementSource;
        while (parent) {
            if (parent.id === measurementId) return true;

            parent = parent.computedMeasurementSource;
        }

        return measurement.computedMeasurementSourceId === measurementId;
    }

    /**
     * Set the value of a field of a measurement the specified measurement id on the provided measurement.
     * If the measurement has the measurement id provided the measurement itself will be updated, otherwise parent measurements will be checked.
     * @param measurement
     * @param measurementId
     * @param field
     * @param value
     */
    public static updateMeasurementField(
        measurement: GenericMeasurement,
        measurementId: string,
        field: string,
        value: unknown
    ): boolean {
        let m = measurement;
        while (m) {
            if (m.id === measurementId) {
                if (m[field] !== value) {
                    m[field] = value;
                    return true;
                }

                return true;
            }

            m = m.computedMeasurementSource;
        }

        return false;
    }
}
